import { useRef, useState } from "react";
import { toast } from "sonner";
import { CardSide } from "@/components/card/CardSide";
import { renderImages, makePdf, browserPrint } from "@/lib/exporter";

const RENDER_SCALE = 4;

// Renders card faces off-screen, then downloads a PDF or opens the print dialog.
export function useExporter() {
  const stageRef = useRef(null);
  const [job, setJob] = useState(null);

  const run = async ({ kind, tag, tpl, pages, data, bleed = 0, filename }) => {
    if (!pages.length) return toast.error("Select at least one card");
    const seen = new Map();
    pages.forEach((p) => p.cells.forEach((c) => seen.set(`${c.key}-${c.side}`, c)));
    const base = { kind: tag || kind, tpl, render: [...seen.values()], data, bleed };
    setJob({ ...base, done: 0, total: base.render.length });
    await new Promise((r) => setTimeout(r, 150));
    try {
      const images = await renderImages(stageRef.current, (done, total) => setJob({ ...base, done, total }));
      if (kind === "pdf") { makePdf(pages, images, filename); toast.success("PDF downloaded"); }
      else browserPrint(pages, images);
    } catch (e) {
      console.error(e);
      toast.error("Export failed — check that all images loaded");
    } finally { setJob(null); }
  };

  const stage = job ? (
    <div ref={stageRef} style={{ position: "fixed", left: -20000, top: 0, pointerEvents: "none" }} aria-hidden>
      {job.render.map((c) => (
        <div key={`${c.key}-${c.side}`} data-render-key={`${c.key}-${c.side}`}
          style={{ width: (job.tpl.width + 2 * job.bleed) * RENDER_SCALE, height: (job.tpl.height + 2 * job.bleed) * RENDER_SCALE }}>
          <CardSide tpl={job.tpl} side={c.side} data={job.data[c.key]} scale={RENDER_SCALE} bleed={job.bleed} />
        </div>
      ))}
    </div>
  ) : null;

  return { run, job, stage };
}
